// Premium-pipeline statuses are shared with @upercut/b2b via @common.
// Re-export them so existing `@modules/jobApplications/statuses` imports keep working.
export {
    PremiumJobApplicationStatus,
    PremiumJobApplicationSubStatus,
    premiumJobApplicationStatusLabels,
    premiumJobApplicationSubStatusLabels,
    premiumJobApplicationStatuses,
    PREMIUM_PIPELINE_STAGES,
} from '@common/statuses/jobApplication';

// ---------------------------------------------------------------------------
// Classic / direct_email pipeline.
//
// NOTE: Classic sub-status *values* intentionally differ from b2b
// (e.g. "ToSchedule" here vs "To Schedule" in b2b). They are persisted in the
// DB, so do not rename them without a migration. That is also why classic
// statuses are not shared via @common — only the premium pipeline is shared.
// ---------------------------------------------------------------------------

export enum JobApplicationStatus {
    New = 'New',
    Interview = 'Interview',
    Trial = 'Trial',
    Offer = 'Offer',
    ProbationPeriod = 'ProbationPeriod',
    Hired = 'Hired',
    Rejected = 'Rejected',
}

export enum JobApplicationSubStatus {
    Talent = 'Talent',
    Contact = 'Contact',
    ToContact = 'ToContact',
    ToFollowUp = 'ToFollowUp',
    NewRejected = 'NewRejected',
    ToSchedule = 'ToSchedule',
    Scheduled = 'Scheduled',
    Conducted = 'Conducted',
    Canceled = 'Canceled',
    InterviewRejected = 'InterviewRejected',
    Completed = 'Completed',
    TrialRejected = 'TrialRejected',
    ToSend = 'ToSend',
    Sent = 'Sent',
    Accepted = 'Accepted',
    OfferRejected = 'OfferRejected',
    Upcoming = 'Upcoming',
    InProgress = 'InProgress',
    AtRisk = 'AtRisk',
    EmployeeResigned = 'EmployeeResigned',
    EmployerTerminated = 'EmployerTerminated',
}

export const jobApplicationStatusLabels = {
    [JobApplicationStatus.New]: 'Candidature',
    [JobApplicationStatus.Interview]: 'Entretien',
    [JobApplicationStatus.Trial]: 'Essai',
    [JobApplicationStatus.Offer]: 'Proposition',
    [JobApplicationStatus.ProbationPeriod]: "Periode d'essai",
    [JobApplicationStatus.Hired]: 'Embauché',
    [JobApplicationStatus.Rejected]: 'Refusé',
};

export const jobApplicationSubStatusLabels = {
    [JobApplicationSubStatus.Talent]: 'Talent',
    [JobApplicationSubStatus.ToContact]: 'A contacter',
    [JobApplicationSubStatus.ToFollowUp]: 'A relancer',
    [JobApplicationSubStatus.NewRejected]: 'Refusé (Contact)',
    [JobApplicationSubStatus.ToSchedule]: 'À planifier',
    [JobApplicationSubStatus.Scheduled]: 'Planifié',
    [JobApplicationSubStatus.Conducted]: 'Réalisé',
    [JobApplicationSubStatus.Canceled]: 'Annulé',
    [JobApplicationSubStatus.InterviewRejected]: 'Refusé (Entretien)',
    [JobApplicationSubStatus.Completed]: 'Réalisé',
    [JobApplicationSubStatus.TrialRejected]: 'Refusé (Essai)',
    [JobApplicationSubStatus.ToSend]: 'À envoyer',
    [JobApplicationSubStatus.Sent]: 'Envoyée',
    [JobApplicationSubStatus.Accepted]: 'Acceptée',
    [JobApplicationSubStatus.OfferRejected]: 'Refusé (Proposition)',
    [JobApplicationSubStatus.Upcoming]: 'À venir',
    [JobApplicationSubStatus.InProgress]: 'En cours',
    [JobApplicationSubStatus.AtRisk]: 'À risque',
    [JobApplicationSubStatus.EmployeeResigned]: 'Rupture salarié',
    [JobApplicationSubStatus.EmployerTerminated]: 'Rupture employeur',
};

export const jobApplicationStatuses = {
    [JobApplicationStatus.New]: [
        JobApplicationSubStatus.Talent,
        JobApplicationSubStatus.ToContact,
        JobApplicationSubStatus.ToFollowUp,
        JobApplicationSubStatus.NewRejected,
    ],
    [JobApplicationStatus.Interview]: [
        JobApplicationSubStatus.ToSchedule,
        JobApplicationSubStatus.Scheduled,
        JobApplicationSubStatus.Conducted,
        JobApplicationSubStatus.Canceled,
        JobApplicationSubStatus.InterviewRejected,
    ],
    [JobApplicationStatus.Trial]: [
        JobApplicationSubStatus.ToSchedule,
        JobApplicationSubStatus.Scheduled,
        JobApplicationSubStatus.Completed,
        JobApplicationSubStatus.Canceled,
        JobApplicationSubStatus.TrialRejected,
    ],
    [JobApplicationStatus.Offer]: [
        JobApplicationSubStatus.ToSend,
        JobApplicationSubStatus.Sent,
        JobApplicationSubStatus.Accepted,
        JobApplicationSubStatus.OfferRejected,
    ],
    [JobApplicationStatus.ProbationPeriod]: [
        JobApplicationSubStatus.Upcoming,
        JobApplicationSubStatus.InProgress,
        JobApplicationSubStatus.AtRisk,
        JobApplicationSubStatus.EmployeeResigned,
        JobApplicationSubStatus.EmployerTerminated,
    ],
    [JobApplicationStatus.Hired]: [],
};

export const optionsJobStatus = Object.keys(JobApplicationStatus).map(status => ({
    label: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
    value: status,
}));

export const optionsJobSubStatus = Object.keys(jobApplicationStatuses).flatMap(status =>
    jobApplicationStatuses[status as keyof typeof jobApplicationStatuses].map(subStatus => ({
        label: jobApplicationSubStatusLabels[subStatus as keyof typeof jobApplicationSubStatusLabels],
        value: subStatus,
        group: status,
        groupLabel: jobApplicationStatusLabels[status as keyof typeof jobApplicationStatusLabels],
    })),
);

/**
 * Classic / direct_email pipeline stage order used by the stepper.
 * `Rejected` is intentionally excluded (terminal step rendered separately).
 */
export const CLASSIC_PIPELINE_STAGES: JobApplicationStatus[] = [
    JobApplicationStatus.New,
    JobApplicationStatus.Interview,
    JobApplicationStatus.Trial,
    JobApplicationStatus.Offer,
    JobApplicationStatus.ProbationPeriod,
    JobApplicationStatus.Hired,
];
